import { useState, useEffect } from 'react';
import { AdminShell } from '@/components/AdminShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { GripVertical, Plus, Trash2, Save } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function AdminPageBuilder() {
  const [pages, setPages] = useState([]);
  const [activeSlug, setActiveSlug] = useState('home');
  const [editData, setEditData] = useState({});
  const [pillars, setPillars] = useState([]);

  useEffect(() => {
    api.get('/admin/pages').then(res => {
      setPages(res.data);
      const home = res.data.find(p => p.slug === 'home');
      if (home) {
        setEditData(home.sections || {});
        setPillars(home.sections?.pillars || []);
      }
    }).catch(console.error);
  }, []);

  const handleTabChange = (slug) => {
    setActiveSlug(slug);
    const page = pages.find(p => p.slug === slug);
    if (page) {
      setEditData(page.sections || {});
      if (slug === 'home') {
        setPillars(page.sections?.pillars || []);
      }
    }
  };

  const handleSave = async () => {
    try {
      const dataToSave = { ...editData };
      if (activeSlug === 'home') {
        dataToSave.pillars = pillars;
      }
      await api.put(`/admin/pages/${activeSlug}`, { sections: dataToSave });
      toast.success('Page updated successfully.');
      const res = await api.get('/admin/pages');
      setPages(res.data);
    } catch (err) {
      toast.error('Failed to save page.');
    }
  };

  const updateNested = (path, value) => {
    const newData = JSON.parse(JSON.stringify(editData));
    const keys = path.split('.');
    let obj = newData;
    for (let i = 0; i < keys.length - 1; i++) {
      if (obj[keys[i]] === undefined) obj[keys[i]] = {};
      obj = obj[keys[i]];
    }
    obj[keys[keys.length - 1]] = value;
    setEditData(newData);
  };

  const renderField = (label, path, type = 'input') => {
    const keys = path.split('.');
    let value = editData;
    for (const k of keys) {
      value = value?.[k];
    }
    return (
      <div key={path}>
        <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">
          {label}
        </label>
        {type === 'textarea' ? (
          <Textarea
            value={value || ''}
            onChange={e => updateNested(path, e.target.value)}
            rows={3}
            className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm resize-none"
          />
        ) : (
          <Input
            value={value || ''}
            onChange={e => updateNested(path, e.target.value)}
            className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm"
          />
        )}
      </div>
    );
  };

  // Pillar management
  const updatePillar = (index, field, value) => {
    const newPillars = [...pillars];
    newPillars[index] = { ...newPillars[index], [field]: value };
    setPillars(newPillars);
  };

  const movePillar = (index, direction) => {
    const newPillars = [...pillars];
    const target = index + direction;
    if (target < 0 || target >= newPillars.length) return;
    [newPillars[index], newPillars[target]] = [newPillars[target], newPillars[index]];
    setPillars(newPillars);
  };

  const addPillar = () => {
    setPillars([...pillars, { label: 'New Pillar', title: 'The practice of...', description: '' }]);
  };

  const removePillar = (index) => {
    if (pillars.length <= 1) {
      toast.error('At least one pillar is required.');
      return;
    }
    setPillars(pillars.filter((_, i) => i !== index));
  };

  return (
    <AdminShell title="Pages">
      <Tabs value={activeSlug} onValueChange={handleTabChange}>
        <TabsList className="bg-[hsl(var(--card))] border border-[rgba(201,168,76,0.12)] mb-6">
          {pages.map(p => (
            <TabsTrigger
              key={p.slug}
              value={p.slug}
              className="font-['Inter'] text-sm data-[state=active]:bg-[rgba(27,77,68,0.3)] data-[state=active]:text-[hsl(var(--gold))]"
              data-testid={`admin-sidebar-pages-${p.slug}`}
            >
              {p.title}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Home Page */}
        <TabsContent value="home">
          <div className="space-y-6">
            <Card className="bg-[hsl(var(--card))] border-[rgba(201,168,76,0.15)]">
              <CardHeader><CardTitle className="font-['Cormorant_Garamond'] text-xl text-[hsl(var(--foreground))]">Hero Section</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {renderField('Headline', 'hero.headline')}
                {renderField('Tagline', 'hero.tagline')}
                {renderField('CTA Text', 'hero.cta_text')}
                {renderField('CTA Link', 'hero.cta_link')}
              </CardContent>
            </Card>

            <Card className="bg-[hsl(var(--card))] border-[rgba(201,168,76,0.15)]">
              <CardHeader><CardTitle className="font-['Cormorant_Garamond'] text-xl text-[hsl(var(--foreground))]">Identity Anchor</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {renderField('Statement', 'identity_anchor.statement')}
                {renderField('Description', 'identity_anchor.description', 'textarea')}
              </CardContent>
            </Card>

            {/* Pillars - Reorderable */}
            <Card className="bg-[hsl(var(--card))] border-[rgba(201,168,76,0.15)]">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-['Cormorant_Garamond'] text-xl text-[hsl(var(--foreground))]">Three Pillars</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addPillar}
                  className="border-[rgba(201,168,76,0.2)] text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))] font-['Inter'] text-xs"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add Pillar
                </Button>
              </CardHeader>
              <CardContent className="space-y-6">
                {pillars.map((pillar, i) => (
                  <div key={pillar.label || `pillar-${i}`} className="p-4 rounded-lg border border-[rgba(201,168,76,0.1)] bg-[rgba(7,10,10,0.3)] space-y-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <GripVertical className="h-4 w-4 text-[hsl(var(--silver))]" />
                        <span className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--gold))]">
                          Pillar {i + 1}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-[hsl(var(--silver))]" onClick={() => movePillar(i, -1)} disabled={i === 0}>
                          <span className="text-xs">Up</span>
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-[hsl(var(--silver))]" onClick={() => movePillar(i, 1)} disabled={i === pillars.length - 1}>
                          <span className="text-xs">Dn</span>
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-[hsl(var(--silver))] hover:text-red-400" onClick={() => removePillar(i)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <label className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-1 block">Label</label>
                      <Input value={pillar.label || ''} onChange={e => updatePillar(i, 'label', e.target.value)} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.15)] text-[hsl(var(--foreground))] font-['Inter'] text-sm" />
                    </div>
                    <div>
                      <label className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-1 block">Title</label>
                      <Input value={pillar.title || ''} onChange={e => updatePillar(i, 'title', e.target.value)} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.15)] text-[hsl(var(--foreground))] font-['Inter'] text-sm" />
                    </div>
                    <div>
                      <label className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-1 block">Description</label>
                      <Textarea value={pillar.description || ''} onChange={e => updatePillar(i, 'description', e.target.value)} rows={2} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.15)] text-[hsl(var(--foreground))] font-['Inter'] text-sm resize-none" />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="bg-[hsl(var(--card))] border-[rgba(201,168,76,0.15)]">
              <CardHeader><CardTitle className="font-['Cormorant_Garamond'] text-xl text-[hsl(var(--foreground))]">Closing Invocation</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {renderField('Text', 'closing_invocation.text', 'textarea')}
                {renderField('CTA Text', 'closing_invocation.cta_text')}
                {renderField('CTA Description', 'closing_invocation.cta_description', 'textarea')}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* About Page */}
        <TabsContent value="about">
          <div className="space-y-6">
            <Card className="bg-[hsl(var(--card))] border-[rgba(201,168,76,0.15)]">
              <CardHeader><CardTitle className="font-['Cormorant_Garamond'] text-xl text-[hsl(var(--foreground))]">Page Header</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {renderField('Headline', 'headline')}
                {renderField('Introduction', 'intro', 'textarea')}
              </CardContent>
            </Card>
            <Card className="bg-[hsl(var(--card))] border-[rgba(201,168,76,0.15)]">
              <CardHeader><CardTitle className="font-['Cormorant_Garamond'] text-xl text-[hsl(var(--foreground))]">Founder Details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {renderField('Name', 'founder.name')}
                {renderField('Title', 'founder.title')}
                {renderField('Bio', 'founder.bio', 'textarea')}
                {renderField('Extended Bio', 'founder.extended_bio', 'textarea')}
                {renderField('Image URL', 'founder.image_url')}
              </CardContent>
            </Card>
            <Card className="bg-[hsl(var(--card))] border-[rgba(201,168,76,0.15)]">
              <CardHeader><CardTitle className="font-['Cormorant_Garamond'] text-xl text-[hsl(var(--foreground))]">Origin Story</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {renderField('Origin Story', 'origin_story', 'textarea')}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Contact Page */}
        <TabsContent value="contact">
          <Card className="bg-[hsl(var(--card))] border-[rgba(201,168,76,0.15)]">
            <CardHeader><CardTitle className="font-['Cormorant_Garamond'] text-xl text-[hsl(var(--foreground))]">Contact Page</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {renderField('Headline', 'headline')}
              {renderField('Description', 'description', 'textarea')}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="mt-6">
        <Button onClick={handleSave} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium px-8 py-5 rounded-lg">
          <Save className="h-4 w-4 mr-2" /> Save Page
        </Button>
      </div>
    </AdminShell>
  );
}
